import { useState, useEffect, useRef } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { buildCollectionWithCache } from '@/app/utils/query-builder';
import { DEFAULT_LANGUAGE_ID } from '@/app/utils/constants';

const initialEmptyValue = {
    contentlets: [],
    total: 0
};

/**
 * Custom hook to manage and fetch a collection of content based on search and filter criteria.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {number} params.limit - The maximum number of items to fetch.
 * @param {string} [params.search=""] - The search term to filter content.
 * @param {Object} [params.filter={}] - An object containing filters to apply to the query.
 * @param {Object} params.queryConfig - Configuration for building the query, including default and search queries.
 * @param {string} params.contentType - The type of content to fetch.
 * @param {Object} [params.initialCollection={}] - Initial collection data to populate the state.
 *
 * @returns {Object} - An object containing the collection and functions to update filters and search terms.
 * @returns {Object} collection - The current collection, containing contentlets and total count.
 * @returns {Function} setFilter - Function to update the filter state.
 * @returns {Function} setSearch - Function to update the search term.
 * @returns {Function} setPage - Function to update the current page.
 */
const useCollection = ({
    limit,
    search = '',
    tag = '',
    filter = {},
    queryConfig,
    contentType,
    initialCollection,
    conhostId,
    depth
}) => {
    const [collection, setCollection] = useState({
        ...initialEmptyValue,
        ...(initialCollection || {})
    });
    const [currentFilter, setCurrentFilter] = useState(() => {
        const initialFilter = { ...filter };
        return initialFilter;
    });
    const [searchTerm, setSearchTerm] = useState(search);
    const [tagTerm, setTagTerm] = useState(tag);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);

    const initialFilterRef = useRef(filter);
    const initialSearchRef = useRef(search);
    const initialTagRef = useRef(tag);
    const initialQueryConfigRef = useRef(queryConfig);
    const prevQueryConfigRef = useRef(queryConfig);
    const initialLimitRef = useRef(limit);
    const initialConhostIdRef = useRef(conhostId);
    const isFirstRenderRef = useRef(true);
    const hasSkippedInitialFetchRef = useRef(false);

    const debouncedSearch = useDebounce(searchTerm, 500);
    const abortControllerRef = useRef(null);

    useEffect(() => {
        if (!contentType) {
            return;
        }

        const isFirstRender = isFirstRenderRef.current;
        isFirstRenderRef.current = false;

        const searchToCheck = isFirstRender ? searchTerm : debouncedSearch;

        const filtersChanged =
            JSON.stringify(currentFilter) !== JSON.stringify(initialFilterRef.current);
        const searchChanged =
            (searchToCheck || '').toLowerCase() !== (initialSearchRef.current || '').toLowerCase();
        const tagChanged =
            (tagTerm || '').toLowerCase() !== (initialTagRef.current || '').toLowerCase();
        const queryConfigChanged =
            JSON.stringify(queryConfig) !== JSON.stringify(initialQueryConfigRef.current);
        const queryConfigChangedFromPrev =
            JSON.stringify(queryConfig) !== JSON.stringify(prevQueryConfigRef.current);
        prevQueryConfigRef.current = queryConfig;
        const limitChanged = limit !== initialLimitRef.current;
        const conhostIdChanged = conhostId !== initialConhostIdRef.current;

        const shouldSkipPage1Fetch =
            initialCollection &&
            page === 1 &&
            !filtersChanged &&
            !searchChanged &&
            !tagChanged &&
            !queryConfigChanged &&
            !limitChanged &&
            !conhostIdChanged;

        if (isFirstRender && shouldSkipPage1Fetch) {
            hasSkippedInitialFetchRef.current = true;
            return;
        }

        if (!isFirstRender && shouldSkipPage1Fetch && hasSkippedInitialFetchRef.current) {
            return;
        }

        if (
            page === 1 &&
            (filtersChanged ||
                searchChanged ||
                tagChanged ||
                queryConfigChanged ||
                limitChanged ||
                conhostIdChanged)
        ) {
            hasSkippedInitialFetchRef.current = false;
        }

        const loweredSearchTerm = (debouncedSearch || '').toLowerCase();
        const loweredTagTerm = tagTerm ? tagTerm.toLowerCase() : undefined;

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();

        const fetchData = async () => {
            try {
                setIsLoading(true);
                const { contentlets: newContentlets, total } = await buildCollectionWithCache({
                    limit,
                    queryConfig,
                    contentType,
                    page,
                    tagTerm: loweredTagTerm,
                    searchTerm: loweredSearchTerm,
                    filter: currentFilter,
                    appendFilter: true,
                    language: DEFAULT_LANGUAGE_ID,
                    conhostId,
                    depth
                });

                if (abortControllerRef.current?.signal.aborted) {
                    return;
                }

                setCollection((prevCollection) => {
                    const preValues =
                        page > 1 && !queryConfigChangedFromPrev ? prevCollection.contentlets : [];

                    const existingIdentifiers = new Set(preValues.map((item) => item.identifier));
                    const uniqueNewContentlets = newContentlets.filter(
                        (item) => !existingIdentifiers.has(item.identifier)
                    );

                    const result = {
                        total,
                        contentlets: [...preValues, ...uniqueNewContentlets]
                    };
                    return result;
                });
            } catch (error) {
                console.error('Error fetching collection:', error);
            } finally {
                if (!abortControllerRef.current?.signal.aborted) {
                    setIsLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentType, debouncedSearch, page, currentFilter, limit, tagTerm, queryConfig, conhostId]);

    const setFilter = (nextFilter) => {
        setCurrentFilter((prev) => {
            const result = typeof nextFilter === 'function' ? nextFilter(prev) : nextFilter;
            return result;
        });
        setPage(1);
    };

    const setSearch = (search) => {
        setSearchTerm(search);
        setPage(1);
    };

    const setTag = (tag) => {
        setTagTerm(tag);
        setPage(1);
    };

    const setPageWrapper = (newPage) => {
        setPage(newPage);
    };

    return { collection, setTag, setFilter, setSearch, setPage: setPageWrapper, isLoading, page };
};

export default useCollection;
